import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';
import type { KBArticleData } from '@atlassian/rovo-content-bridge-api-commands/commands/jsm-create-kb-sync-commands';

export type ArticlePublishStatus = 'DRAFT' | 'CURRENT';

const EMPTY_ARTICLE_ADF = {
	type: 'doc',
	version: 1,
	content: [],
} as const;

const isAdfDocNode = (
	value: unknown,
): value is { type: 'doc'; version: number; content: unknown[] } =>
	typeof value === 'object' &&
	value !== null &&
	'type' in value &&
	(value as { type?: unknown }).type === 'doc';

const createParagraphDocument = (text: string) => ({
	type: 'doc',
	version: 1,
	content: [
		{
			type: 'paragraph',
			content: [{ type: 'text', text }],
		},
	],
});

const JSD_APOLLO_STARGATE_CONTEXT_ROOT = '/gateway/api/jsd-apollo-stargate/sharded/workspace';

const getCreateArticlePath = ({
	projectId,
	activationId,
	spaceKey,
}: {
	projectId: number;
	activationId: string;
	spaceKey: string;
}): string =>
	`${JSD_APOLLO_STARGATE_CONTEXT_ROOT}/${activationId}/api/project/${projectId}/spaceKey/${encodeURIComponent(
		spaceKey,
	)}/article`;

const getArticleAdfValue = (article: KBArticleData): string => {
	const rawContent = article.body as unknown;
	if (!rawContent) {
		return JSON.stringify(EMPTY_ARTICLE_ADF);
	}

	// If upstream already provided parsed ADF, preserve it as-is.
	if (isAdfDocNode(rawContent)) {
		return JSON.stringify(rawContent);
	}

	if (typeof rawContent !== 'string') {
		return JSON.stringify(EMPTY_ARTICLE_ADF);
	}

	try {
		const parsedContent: unknown = JSON.parse(rawContent);
		if (isAdfDocNode(parsedContent)) {
			return JSON.stringify(parsedContent);
		}
		return JSON.stringify(createParagraphDocument(rawContent));
	} catch {
		return JSON.stringify(createParagraphDocument(rawContent));
	}
};

export const publishConfluenceArticle = async ({
	projectId,
	activationId,
	spaceKey,
	article,
	status,
}: {
	projectId: number;
	activationId: string;
	spaceKey: string;
	article: KBArticleData;
	status: ArticlePublishStatus;
}): Promise<void> => {
	await performHttpFetch({
		path: getCreateArticlePath({
			projectId,
			activationId,
			spaceKey,
		}),
		options: {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				type: 'page',
				title: article.title,
				status,
				body: getArticleAdfValue(article),
				isAiGenerated: true,
			}),
		},
	});
};
